import { useEffect, useState } from "react";
import { ActionIcon, Container, Group, Loader } from "@mantine/core";
import { IconPencil, IconPlus, IconTrash } from "@tabler/icons-react";
import { DataTable } from "mantine-datatable";
import { notifications } from "@mantine/notifications";
import { useNavigate } from "react-router-dom";
import HttpClient from "../helpers/HttpClient";
import { BookingType } from "../types/BookingModel";
import { ConfirmDialog } from "../components/ConfirmDialog";

function BookingTypeList() {
  const [loading, setLoading] = useState(true);
  const [bookingTypes, setBookingTypes] = useState<BookingType[]>([]);
  const [confirmId, setConfirmId] = useState<number | null>(null);
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await new HttpClient<BookingType>("/bookingType").getAll();
      setBookingTypes([...result].sort((a, b) => a.title.localeCompare(b.title)));
    } catch {
      notifications.show({ message: "Fehler beim Laden der Buchungsarten", color: "red" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleDelete = async () => {
    if (confirmId === null) return;
    try {
      const res = await new HttpClient<BookingType>("/bookingType").delete(confirmId);
      if (res.status !== 200) throw new Error();
      setBookingTypes(prev => prev.filter(t => Number(t.id) !== confirmId));
      notifications.show({ message: "Buchungsart gelöscht", color: "green" });
    } catch {
      notifications.show({ message: "Löschen fehlgeschlagen", color: "red" });
    } finally {
      setConfirmId(null);
    }
  };

  return (
    <Container fluid pb={10} mt={10}>
      <ConfirmDialog
        opened={confirmId !== null}
        onClose={() => setConfirmId(null)}
        onConfirm={handleDelete}
        title="Buchungsart löschen"
        message="Soll diese Buchungsart wirklich gelöscht werden?"
        confirmLabel="Löschen"
        cancelLabel="Abbrechen"
      />
      <Group gap={25} visibleFrom="xs">
        <h1>Buchungsarten</h1>
        <ActionIcon
          variant="filled"
          size="xl"
          radius="xl"
          aria-label="Neue Buchungsart"
          onClick={() => navigate("/bookingtype/create")}
        >
          <IconPlus style={{ width: "70%", height: "70%" }} stroke={1.5} />
        </ActionIcon>
      </Group>
      {loading ? (
        <Loader size="md" />
      ) : (
        <DataTable
          withTableBorder
          borderRadius="sm"
          withColumnBorders
          striped
          highlightOnHover
          records={bookingTypes}
          idAccessor="id"
          columns={[
            { accessor: "title", title: "Bezeichnung" },
            {
              accessor: "multiplier",
              title: "Multiplikator",
              width: "15%",
            },
            {
              accessor: "actions",
              title: "",
              width: "8%",
              render: (bt) => (
                <Group gap={4} justify="center">
                  <ActionIcon
                    variant="light"
                    color="blue"
                    onClick={() => navigate(`/bookingtype/edit/${bt.id}`)}
                    aria-label="Bearbeiten"
                  >
                    <IconPencil size={18} />
                  </ActionIcon>
                  <ActionIcon
                    variant="light"
                    color="red"
                    onClick={() => setConfirmId(Number(bt.id))}
                    aria-label="Löschen"
                  >
                    <IconTrash size={18} />
                  </ActionIcon>
                </Group>
              ),
            },
          ]}
        />
      )}
    </Container>
  );
}

export default BookingTypeList;
